// =============================================================================
// EmployeeComplianceScreen — /hr/compliance  (T-390 + T-393)
// The polished front door for people compliance. T-393 turned the employee
// list into PEOPLE CARDS: every active person on our sites — internal
// employees AND subbie workers — as a card with photo (uploadable, manager+),
// role + company, and one overall verdict: green = site ready, amber =
// expiring soon, red = overdue / company paperwork, grey = pending or no
// tickets loaded. Employees with ZERO licences now show too (they used to be
// invisible). Click-through: internal → licence register page, subbie worker →
// their company's hub cockpit. Dashboard over the proven T-004/T-381 machinery.
// =============================================================================

import { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck, AlertTriangle, CalendarClock, FileCheck2, ChevronRight, Users,
  Camera, Plus, ShieldCheck,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

type LicStatus = "current" | "expiring" | "overdue" | "pending";

function statusOf(l: any): LicStatus {
  const today = new Date().toISOString().slice(0, 10);
  if (l.expiry_date && l.expiry_date < today) return "overdue";
  if (!l.verified_at) return "pending";
  if (l.expiry_date) {
    const days = Math.round((new Date(l.expiry_date).getTime() - Date.now()) / 86400000);
    if (days <= 30) return "expiring";
  }
  return "current";
}

const STATUS_META: Record<LicStatus, { label: string; colour: string; text: string }> = {
  current:  { label: "Current",  colour: "#1a8f4c", text: "text-ok" },
  expiring: { label: "Expiring ≤30d", colour: "#d97706", text: "text-warn" },
  overdue:  { label: "Overdue",  colour: "#dc2626", text: "text-danger" },
  pending:  { label: "Pending verification", colour: "#6b7280", text: "text-text-muted" },
};

// ---------------------------------------------------------------------------
// Person-card model — one verdict per human (T-393)
// ---------------------------------------------------------------------------
type Verdict = "ready" | "expiring" | "overdue" | "pending" | "none";

const VERDICT_META: Record<Verdict, { label: string; pillTone: any; border: string }> = {
  ready:    { label: "Site ready",       pillTone: "won",     border: "border-l-ok" },
  expiring: { label: "Expiring soon",    pillTone: "warning", border: "border-l-warn" },
  overdue:  { label: "Overdue",          pillTone: "lost",    border: "border-l-danger" },
  pending:  { label: "Pending checks",   pillTone: "default", border: "border-l-line" },
  none:     { label: "No tickets loaded", pillTone: "default", border: "border-l-line" },
};

const VERDICT_SCORE: Record<Verdict, number> = { overdue: 0, expiring: 1, pending: 2, none: 3, ready: 4 };

interface PersonCard {
  key:      string;
  kind:     "employee" | "subbie_worker";
  id:       string;
  name:     string;
  role:     string;
  company:  string;
  isSubbie: boolean;
  photoPath: string | null;   // storage path (people-photos) or full http URL
  verdict:  Verdict;
  issues:   string[];         // named, dated problems (first few shown)
  to:       string;           // click-through route
}

// Deterministic colour for initials avatars
const AVATAR_COLOURS = ["#1a8f4c", "#d97706", "#2563eb", "#7c3aed", "#0e7490", "#b91c1c", "#4d7c0f"];
function avatarColour(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLOURS[h % AVATAR_COLOURS.length];
}

// ---------------------------------------------------------------------------
// Subbie compliance at a glance (T-390) — kept: companies-level strip
// ---------------------------------------------------------------------------
function SubbieStrip({ subbies }: { subbies: any[] }) {
  if (subbies.length === 0) return null;
  const attention = subbies.filter(s => s.state !== "ok");
  return (
    <Link to="/subbies" className="block mb-5">
      <Card className={attention.length > 0 ? "border-warn/40" : ""}>
        <div className="p-3.5 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition-colors rounded-lg">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Subbie companies</div>
          <span className="text-[13px] text-text">{subbies.length} compan{subbies.length === 1 ? "y" : "ies"}</span>
          {attention.length > 0
            ? <span className="text-[12px] text-warn">{attention.length} need attention ({attention.slice(0, 3).map(s => s.trading_name || s.legal_name).join(", ")}{attention.length > 3 ? "…" : ""})</span>
            : <span className="text-[12px] text-ok">all compliant</span>}
          <span className="ml-auto text-[12px] text-accent inline-flex items-center gap-0.5">Subbie hub <ChevronRight size={13} /></span>
        </div>
      </Card>
    </Link>
  );
}

export default function EmployeeComplianceScreen() {
  const user = useRequireAuth();
  const queryClient = useQueryClient();
  const isManager = hasRole(user, "manager");

  const [filter, setFilter] = useState<"all" | "attention" | "coretel" | "subbie">("all");
  const [signedPhotos, setSignedPhotos] = useState<Record<string, string>>({});
  const [uploadTarget, setUploadTarget] = useState<{ kind: "employee" | "subbie_worker"; id: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ----- Queries -------------------------------------------------------------
  const { data: lics = [], isLoading: licsLoading } = useQuery({
    queryKey: ["employee_compliance_centre"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select("licence_id, employee_id, licence_number, expiry_date, verified_at, licence_types(display_name), employees!inner(employee_id, is_active)")
        .eq("is_active", true)
        .eq("employees.is_active", true);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: employees = [], isLoading: empLoading } = useQuery({
    queryKey: ["compliance_centre_employees"],
    queryFn: async () => {
      // proven select shape: PeopleScreen.tsx (v_employees + subbie company embed)
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, primary_role_name, employment_type, is_active, profile_photo_url, subcontractor_companies(legal_name, trading_name)")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: workers = [], isLoading: workersLoading } = useQuery({
    queryKey: ["compliance_centre_workers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subbie_workers")
        .select("worker_id, subcontractor_company_id, first_name, last_name, job_title, is_active, photo_path, tickets:subbie_worker_tickets(ticket_id, ticket_type, expiry_date)")
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: subbies = [] } = useQuery({
    queryKey: ["compliance_centre_subbies"],
    queryFn: async () => {
      const { data } = await supabase.from("v_subbie_compliance")
        .select("subcontractor_company_id, trading_name, legal_name, state");
      return (data ?? []) as any[];
    },
  });

  // ----- Licence status roll-ups (drive donut + KPIs) ------------------------
  const withStatus = useMemo(() => lics.map(r => ({ ...r, _status: statusOf(r) as LicStatus })), [lics]);

  const counts = useMemo(() => {
    const c: Record<LicStatus, number> = { current: 0, expiring: 0, overdue: 0, pending: 0 };
    for (const r of withStatus) c[r._status as LicStatus]++;
    return c;
  }, [withStatus]);

  const licsByEmployee = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of withStatus) {
      const arr = m.get(r.employee_id) ?? [];
      arr.push(r);
      m.set(r.employee_id, arr);
    }
    return m;
  }, [withStatus]);

  const subbieStateById = useMemo(
    () => new Map(subbies.map((s: any) => [s.subcontractor_company_id, s])),
    [subbies],
  );

  // ----- Build the unified people cards --------------------------------------
  const cards = useMemo<PersonCard[]>(() => {
    const today = new Date().toISOString().slice(0, 10);
    const soon  = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const out: PersonCard[] = [];

    for (const e of employees) {
      const els = licsByEmployee.get(e.employee_id) ?? [];
      const overdue  = els.filter(l => l._status === "overdue");
      const expiring = els.filter(l => l._status === "expiring");
      const pending  = els.filter(l => l._status === "pending");
      const issues: string[] = [
        ...overdue.map(l => `✗ ${l.licence_types?.display_name ?? "Licence"} — expired ${fmtDate(l.expiry_date)}`),
        ...expiring.map(l => `⚠ ${l.licence_types?.display_name ?? "Licence"} — expires ${fmtDate(l.expiry_date)}`),
        ...(pending.length > 0 ? [`${pending.length} awaiting verification`] : []),
      ];
      const verdict: Verdict =
        overdue.length ? "overdue"
        : expiring.length ? "expiring"
        : pending.length ? "pending"
        : els.length === 0 ? "none"
        : "ready";
      const co = e.subcontractor_companies;
      out.push({
        key: `e-${e.employee_id}`,
        kind: "employee",
        id: e.employee_id,
        name: `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        role: (e.primary_role_name ?? e.employment_type ?? "").replace(/_/g, " "),
        company: co ? (co.trading_name || co.legal_name) : "Coretel",
        isSubbie: !!co,
        photoPath: e.profile_photo_url ?? null,
        verdict, issues,
        to: `/hr/licences/${e.employee_id}`,
      });
    }

    for (const w of workers) {
      const tks = (w.tickets ?? []) as any[];
      const overdue  = tks.filter(t => t.expiry_date && t.expiry_date < today);
      const expiring = tks.filter(t => t.expiry_date && t.expiry_date >= today && t.expiry_date <= soon);
      const co = subbieStateById.get(w.subcontractor_company_id);
      const coBad     = co && (co.state === "expired" || co.state === "no_insurance");
      const coPending = co && co.state === "pending_approval";
      const issues: string[] = [
        ...(coBad ? [`✗ ${co.trading_name || co.legal_name} — company paperwork ${co.state === "expired" ? "expired" : "missing"}`] : []),
        ...overdue.map(t => `✗ ${t.ticket_type} — expired ${fmtDate(t.expiry_date)}`),
        ...expiring.map(t => `⚠ ${t.ticket_type} — expires ${fmtDate(t.expiry_date)}`),
        ...(coPending ? ["company awaiting Coretel approval"] : []),
      ];
      const verdict: Verdict =
        (coBad || overdue.length) ? "overdue"
        : expiring.length ? "expiring"
        : coPending ? "pending"
        : tks.length === 0 ? "none"
        : "ready";
      out.push({
        key: `w-${w.worker_id}`,
        kind: "subbie_worker",
        id: w.worker_id,
        name: `${w.first_name ?? ""} ${w.last_name ?? ""}`.trim(),
        role: w.job_title ?? "",
        company: co ? (co.trading_name || co.legal_name) : "Subbie",
        isSubbie: true,
        photoPath: w.photo_path ?? null,
        verdict, issues,
        to: `/subbies/${w.subcontractor_company_id}`,
      });
    }

    return out.sort((a, b) =>
      VERDICT_SCORE[a.verdict] - VERDICT_SCORE[b.verdict] || a.name.localeCompare(b.name));
  }, [employees, workers, licsByEmployee, subbieStateById]);

  const visibleCards = useMemo(() => {
    switch (filter) {
      case "attention": return cards.filter(c => c.verdict === "overdue" || c.verdict === "expiring" || c.verdict === "pending");
      case "coretel":   return cards.filter(c => !c.isSubbie);
      case "subbie":    return cards.filter(c => c.isSubbie);
      default:          return cards;
    }
  }, [cards, filter]);

  const readyCount = cards.filter(c => c.verdict === "ready").length;

  // ----- Photo signed URLs ---------------------------------------------------
  useEffect(() => {
    const paths = cards.map(c => c.photoPath)
      .filter((p): p is string => !!p && !p.startsWith("http"));
    if (paths.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage.from("people-photos").createSignedUrls(paths, 3600);
      if (cancelled || !data) return;
      const map: Record<string, string> = {};
      for (const item of data) if (item.path && item.signedUrl) map[item.path] = item.signedUrl;
      setSignedPhotos(map);
    })();
    return () => { cancelled = true; };
  }, [cards]);

  function photoUrl(c: PersonCard): string | undefined {
    if (!c.photoPath) return undefined;
    if (c.photoPath.startsWith("http")) return c.photoPath;
    return signedPhotos[c.photoPath];
  }

  // ----- Photo upload (manager+, via app.set_person_photo RPC) ---------------
  function pickPhoto(e: React.MouseEvent, c: PersonCard) {
    e.preventDefault();
    e.stopPropagation();
    setUploadTarget({ kind: c.kind, id: c.id });
    fileRef.current?.click();
  }

  async function onPhotoChosen(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !uploadTarget) return;
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const folder = uploadTarget.kind === "employee" ? "employees" : "subbie-workers";
      const path = `${folder}/${uploadTarget.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("people-photos")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      const { error: rpcErr } = await supabase.schema("app").rpc("set_person_photo", {
        p_kind: uploadTarget.kind, p_id: uploadTarget.id, p_path: path,
      });
      if (rpcErr) throw rpcErr;
      queryClient.invalidateQueries({ queryKey: ["compliance_centre_employees"] });
      queryClient.invalidateQueries({ queryKey: ["compliance_centre_workers"] });
    } catch (err: any) {
      alert(`Photo upload failed: ${err?.message ?? err}`);
    } finally {
      setUploadTarget(null);
    }
  }

  // ----- Donut ---------------------------------------------------------------
  const total = withStatus.length || 1;
  const segments = useMemo(() => {
    const order: LicStatus[] = ["current", "expiring", "overdue", "pending"];
    const C = 2 * Math.PI * 40;
    let offset = 0;
    return order.filter(s => counts[s] > 0).map(s => {
      const frac = counts[s] / total;
      const seg = { status: s, dash: `${frac * C} ${C}`, offset: -offset * C, colour: STATUS_META[s].colour };
      offset += frac;
      return seg;
    });
  }, [counts, total]);

  if (!user) return null;
  if (!isManager) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={FileCheck2} title="Managers only" description="Employee compliance is restricted to manager-and-above roles." /></Card></div>;
  }

  const loading = licsLoading || empLoading || workersLoading;

  return (
    <div className="p-4 md:p-6 max-w-[1150px] mx-auto pb-20">
      {/* hidden shared file input for card photo uploads */}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhotoChosen} />

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BadgeCheck size={20} className="text-accent" /> Compliance centre
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Everyone on our sites — our people and subbie crews — with one verdict each: ready, expiring, or overdue.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* The matrix is the "why" behind every verdict on this page. */}
          <Link to="/hr/compliance/matrix"><Button icon={ShieldCheck}>Compliance matrix</Button></Link>
          <Link to="/people/new"><Button variant="secondary" icon={Plus}>Add employee</Button></Link>
          <Link to="/hr/licences"><Button variant="secondary" icon={FileCheck2}>Licence register</Button></Link>
          <Link to="/compliance"><Button variant="secondary" icon={CalendarClock}>Compliance calendar</Button></Link>
        </div>
      </div>

      <SubbieStrip subbies={subbies} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="People on the books" value={cards.length} icon={Users} />
        <KPI label="Site ready" value={readyCount} icon={BadgeCheck} />
        <KPI label="Expiring ≤ 30 days" value={counts.expiring} icon={CalendarClock} accent={counts.expiring > 0} />
        <KPI label="Overdue" value={counts.overdue} icon={AlertTriangle} accent={counts.overdue > 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start">
        {/* Donut + legend (internal licence statuses) */}
        <Card>
          <div className="p-4 flex flex-col items-center">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold self-start mb-2">Licence status</div>
            <svg viewBox="0 0 100 100" className="w-40 h-40 -rotate-90">
              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--line, #333)" strokeWidth="12" opacity="0.25" />
              {segments.map(s => (
                <circle key={s.status} cx="50" cy="50" r="40" fill="none"
                  stroke={s.colour} strokeWidth="12"
                  strokeDasharray={s.dash} strokeDashoffset={s.offset} strokeLinecap="butt" />
              ))}
            </svg>
            <div className="text-[20px] font-semibold -mt-24 mb-16 rotate-0 text-text">{withStatus.length}</div>
            <div className="space-y-1 self-start w-full mt-2">
              {(Object.keys(STATUS_META) as LicStatus[]).map(s => (
                <div key={s} className="flex items-center gap-2 text-[12px]">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_META[s].colour }} />
                  <span className="text-text-muted flex-1">{STATUS_META[s].label}</span>
                  <span className={`font-mono ${STATUS_META[s].text}`}>{counts[s]}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* People cards — T-393 */}
        <div>
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {([
              ["all", `All (${cards.length})`],
              ["attention", `Needs attention (${cards.filter(c => c.verdict !== "ready" && c.verdict !== "none").length})`],
              ["coretel", "Coretel"],
              ["subbie", "Subbie crews"],
            ] as const).map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  filter === k
                    ? "bg-accent/15 border-accent/40 text-accent font-medium"
                    : "border-line text-text-muted hover:text-text"
                }`}>
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : visibleCards.length === 0 ? (
            <Card><EmptyState icon={Users} title="Nobody here" description={filter === "all" ? "Add employees or subbie workers to start tracking." : "Nobody matches this filter."} /></Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {visibleCards.map(c => {
                const meta = VERDICT_META[c.verdict];
                const url = photoUrl(c);
                return (
                  <Link key={c.key} to={c.to} className="block">
                    <Card className={`h-full border-l-2 ${meta.border}`}>
                      <div className="p-3 flex items-start gap-3 hover:bg-ink-raised/40 transition-colors rounded-lg h-full">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          {url ? (
                            <img src={url} alt={c.name} className="w-12 h-12 rounded-full object-cover border border-line" />
                          ) : (
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-[15px] font-semibold"
                              style={{ background: avatarColour(c.name) }}
                            >
                              {c.name.split(" ").map(p => p[0]).filter(Boolean).slice(0, 2).join("")}
                            </div>
                          )}
                          {isManager && (
                            <button
                              onClick={(e) => pickPhoto(e, c)}
                              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-ink-raised border border-line flex items-center justify-center hover:border-accent"
                              title={url ? "Replace photo" : "Add photo"}
                              aria-label="Upload profile photo"
                            >
                              <Camera size={11} className="text-text-muted" />
                            </button>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14px] font-medium text-text">{c.name}</span>
                            <Pill label={meta.label} tone={meta.pillTone} small />
                          </div>
                          <div className="text-[11.5px] text-text-muted mt-0.5 truncate">
                            {[c.role, c.company].filter(Boolean).join(" · ")}
                          </div>
                          {c.issues.length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {c.issues.slice(0, 2).map((line, i) => (
                                <div key={i} className={`text-[11.5px] ${line.startsWith("✗") ? "text-danger" : line.startsWith("⚠") ? "text-warn" : "text-text-muted"}`}>
                                  {line}
                                </div>
                              ))}
                              {c.issues.length > 2 && (
                                <div className="text-[11px] text-text-dim">+{c.issues.length - 2} more…</div>
                              )}
                            </div>
                          )}
                        </div>
                        <ChevronRight size={14} className="text-text-dim flex-shrink-0 mt-1" />
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="mt-3 text-[11px] text-text-dim italic">
            Add subbie workers on their company's page in the <Link to="/subbies" className="text-accent hover:underline">Subbie hub</Link>.
            Camera badge on a card = load a profile photo.
          </div>
        </div>
      </div>
    </div>
  );
}
